import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Button, ListItem, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { UserWithRoles } from '~/modules/common/components/User';
import IconLinkButton from '~/modules/resourcing/common/components/IconLinkButton';
import { useSmartFitDialog } from '~/modules/resourcing/common/hooks';
import {
  hasAcceptActionAllowed,
  hasProposeActionAllowed
} from '~/modules/resourcing/common/components/ResourceMatchItem/permissionUtil';
import { Decimal } from '~/modules/common/components';
import AcceptWithSmartFitDialog from '~/modules/resourcing/common/components/AcceptWithSmartFitDialog';
import ProposeWithSmartFitDialog from '~/modules/resourcing/common/components/ProposeWithSmartFitDialog';
import RESOURCE_MATCH_POPOVER_BUTTON_ACTION from '../enum';
import { useResourceMatchPopoverStyles } from './useStyles';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0.75, 0)
  },
  numericText: {
    fontWeight: theme.typography.fontWeightMedium
  },
  availabilityLow: {
    color: theme.palette.error.main
  },
  button: {
    color: theme.palette.common.white,
    fontSize: theme.typography.fontWeightMedium,
    backgroundColor: theme.palette.primary.main,
    padding: theme.spacing(0.5, 1.25),
    boxShadow: theme.shadows[2],
    marginLeft: theme.spacing(3.25),
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: theme.shadows[4]
    }
  }
}));

const useUserWithRolesStyles2 = makeStyles(theme => ({
  root: {
    paddingRight: theme.spacing(1)
  }
}));

const useIconLinkButtonStyles = makeStyles(theme => ({
  openButton: {
    color: theme.palette.text.primary,
    opacity: '0.8'
  }
}));

export const ResourceMatchListRow = ({
  me,
  resourceRequest,
  resourceMatch,
  user,
  onClose,
  allocationChartRef,
  buttonType,
  project
}) => {
  const classes = useStyles();
  const commonClasses = useResourceMatchPopoverStyles();
  const userWithRolesClasses2 = useUserWithRolesStyles2();
  const iconLinkButtonClasses = useIconLinkButtonStyles();
  const { formatMessage } = useIntl();

  const {
    isSmartFitOpen,
    smartFitState,
    openSmartFit,
    closeSmartFit
  } = useSmartFitDialog();

  const isProposeButtonType =
    buttonType === RESOURCE_MATCH_POPOVER_BUTTON_ACTION.PROPOSE;

  const userDrawerLabel = formatMessage(
    {
      id: 'resourceMatchPopover.drawer'
    },
    { user: user.displayText }
  );
  const hasPermission =
    hasProposeActionAllowed(me) || hasAcceptActionAllowed(project);

  const onButtonAction = useCallback(() => {
    openSmartFit({
      resourceRequest,
      resourceUser: user,
      ...(!isProposeButtonType && { allocationChartRef })
    });
  }, [
    openSmartFit,
    resourceRequest,
    user,
    isProposeButtonType,
    allocationChartRef
  ]);

  const handleSmartFitClose = useCallback(() => {
    closeSmartFit();
    onClose();
  }, [closeSmartFit, onClose]);

  const isLowAvailability =
    resourceMatch.availabilityPercentageInDateRange < resourceRequest.load;

  return (
    <>
      <ListItem
        data-qe-id={`${resourceMatch.id}_ResourceMatchListRow`}
        className={classes.root}
      >
        <div className={commonClasses.resourceColumn}>
          <UserWithRoles classes={userWithRolesClasses2} user={user} />
          <IconLinkButton
            classes={iconLinkButtonClasses}
            ariaLabel={userDrawerLabel}
            toLink={`#/resource/${user.slug}`}
          />
        </div>
        <div className={commonClasses.smartMatchColumn}>
          <Typography variant="body2">
            {formatMessage(
              {
                id: 'resourceMatchPopover.score'
              },
              { score: resourceMatch.score }
            )}
          </Typography>
        </div>
        <div className={commonClasses.availabilityColumn}>
          <Typography
            variant="body2"
            className={isLowAvailability ? classes.availabilityLow : undefined}
          >
            {formatMessage(
              {
                id: 'resourceMatchPopover.availabilityVal'
              },
              {
                availability: (
                  <Decimal
                    key="availability"
                    value={resourceMatch.availabilityPercentageInDateRange}
                  />
                )
              }
            )}
          </Typography>
        </div>
        {hasPermission && (
          <Button
            variant="contained"
            size="small"
            color="primary"
            className={classes.button}
            onClick={onButtonAction}
          >
            <Typography variant="subtitle2">
              {formatMessage({
                id: isProposeButtonType
                  ? 'resourceMatchPopover.propose'
                  : 'resourceMatchPopover.accept'
              })}
            </Typography>
          </Button>
        )}
      </ListItem>
      {isSmartFitOpen &&
        (isProposeButtonType ? (
          <ProposeWithSmartFitDialog
            smartFitState={smartFitState}
            onClose={handleSmartFitClose}
          />
        ) : (
          <AcceptWithSmartFitDialog
            smartFitState={smartFitState}
            onClose={handleSmartFitClose}
          />
        ))}
    </>
  );
};

ResourceMatchListRow.propTypes = {
  me: PropTypes.object.isRequired,
  resourceRequest: PropTypes.object.isRequired,
  resourceMatch: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  allocationChartRef: PropTypes.object,
  buttonType: PropTypes.oneOf([
    RESOURCE_MATCH_POPOVER_BUTTON_ACTION.ACCEPT,
    RESOURCE_MATCH_POPOVER_BUTTON_ACTION.PROPOSE
  ]).isRequired,
  project: PropTypes.object.isRequired
};

export default ResourceMatchListRow;
